import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import authBg from '@/components/images/background-auth/01-background-auth.jpg';
import onboardingLogo from '@/components/images/logo/logo-orcamigo-onboarding.png';

/**
 * Landing page for the Supabase password-recovery link.
 *
 * The recovery e-mail redirects here with the recovery tokens in the URL (hash
 * tokens for the implicit flow, or a `?code=` for PKCE). The global Supabase
 * client uses detectSessionInUrl, which races with this page: whichever code
 * touches the URL first consumes it. To be deterministic we capture the URL
 * params on the FIRST render — before anything can strip them — and then
 * establish the session explicitly, falling back to whatever the client may
 * have already set up. If none of that yields a session, the link is invalid.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();

  // Snapshot the recovery params on first render, before detectSessionInUrl can
  // strip the hash/query.
  const [recovery] = useState(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const query = new URLSearchParams(window.location.search);
    return {
      accessToken: hash.get('access_token'),
      refreshToken: hash.get('refresh_token'),
      code: query.get('code'),
      error: hash.get('error') || query.get('error'),
      errorDescription: hash.get('error_description') || query.get('error_description'),
    };
  });

  const [ready, setReady] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let settled = false;

    const markReady = () => {
      if (settled) return;
      settled = true;
      setReady(true);
    };
    const markInvalid = () => {
      if (settled) return;
      settled = true;
      setInvalid(true);
    };

    // A late PASSWORD_RECOVERY / SIGNED_IN (e.g. detectSessionInUrl finishing
    // after we subscribed) still flips the page to ready.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
        markReady();
      }
    });

    const establish = async () => {
      // The link itself reported an error (expired/used/denied).
      if (recovery.error) {
        console.warn('[ResetPassword] recovery link error:', recovery.error, recovery.errorDescription);
        markInvalid();
        return;
      }

      // 1) detectSessionInUrl may have already won the race and set a session.
      const { data } = await supabase.auth.getSession();
      if (settled) return;
      if (data.session) {
        markReady();
        return;
      }

      // 2) Implicit flow: set the session from the captured hash tokens.
      if (recovery.accessToken && recovery.refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: recovery.accessToken,
          refresh_token: recovery.refreshToken,
        });
        if (settled) return;
        if (!error) {
          markReady();
          return;
        }
        console.warn('[ResetPassword] setSession failed:', error.message);
      }

      // 3) PKCE flow: exchange the code for a session.
      if (recovery.code) {
        const { error } = await supabase.auth.exchangeCodeForSession(recovery.code);
        if (settled) return;
        if (!error) {
          markReady();
          return;
        }
        console.warn('[ResetPassword] exchangeCodeForSession failed:', error.message);
      }
      // Otherwise, wait for a possible late auth event; the timeout decides.
    };

    establish();

    // Grace window for a late auth event before declaring the link invalid.
    const timeout = setTimeout(markInvalid, 6000);

    return () => {
      sub.subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [recovery]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast.warning('Preencha todos os campos');
      return;
    }
    if (password.length < 6) {
      toast.warning('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      toast.warning('As senhas não coincidem');
      return;
    }

    setIsSaving(true);
    const { error } = await supabase.auth.updateUser({ password });
    setIsSaving(false);

    if (error) {
      toast.error(`Erro ao redefinir a senha: ${error.message}`);
      return;
    }

    toast.success('Senha redefinida com sucesso!');
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center px-4 py-8">
      {/* Background image */}
      <div
        className="fixed inset-0 bg-cover bg-center z-0"
        style={{ backgroundImage: `url(${authBg})` }}
      />
      {/* Dark overlay */}
      <div
        className="fixed inset-0 z-10 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, transparent 0%, rgba(15, 15, 15, 0.4) 40%, rgba(15, 15, 15, 0.95) 100%)',
        }}
      />

      <div className="relative z-20 mb-8">
        <img src={onboardingLogo} alt="Orçamigo" className="h-10 w-auto" />
      </div>

      <Card className="relative z-20 w-full max-w-md">
        <CardHeader>
          <h1 className="text-lg font-semibold">Redefinir senha</h1>
          <p className="text-sm text-muted-foreground">
            Escolha uma nova senha para a sua conta.
          </p>
        </CardHeader>

        <CardContent>
          {invalid ? (
            <div className="space-y-4 text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
              <p className="text-sm text-muted-foreground">
                Este link de redefinição é inválido ou expirou. Solicite um novo
                na tela de login.
              </p>
              <Button3D color="gray" className="w-full" onClick={() => navigate('/auth', { replace: true })}>
                Voltar para o login
              </Button3D>
            </div>
          ) : !ready ? (
            <div className="flex flex-col items-center gap-3 py-6">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Validando seu link...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative pt-1">
                <Label htmlFor="new-password" className="absolute left-3 -top-2 z-10 bg-card px-1 text-xs">
                  Nova senha
                </Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="relative pt-1">
                <Label htmlFor="confirm-password" className="absolute left-3 -top-2 z-10 bg-card px-1 text-xs">
                  Confirmar nova senha
                </Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              <Button3D type="submit" className="w-full" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Redefinir senha'
                )}
              </Button3D>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
